import React from 'react';
import { DollarSign, Calendar, Percent } from 'lucide-react';

/**
 * Компонент с базовыми параметрами для калькулятора инфляции
 */
const BasicParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  inflationRatesByYear
}) => {
  // Получаем минимальный и максимальный год из доступных данных
  const years = Object.keys(inflationRatesByYear).map(Number);
  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);
  const currentYear = new Date().getFullYear();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {/* Сумма */}
      <div>
        <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <DollarSign className="w-4 h-4 mr-2 text-red-500" />
          Сумма (₽)
        </label>
        <input
          type="text"
          id="amount"
          name="amount"
          value={formData.amount}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.amount ? 'input-error' : ''}`}
          placeholder="Введите сумму"
        />
        {errors.amount && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.amount}
          </p>
        )}
      </div>

      {/* Тип расчета */}
      <div className="col-span-full">
        <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Тип расчета
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="calculationType"
              value="depreciation"
              checked={formData.calculationType === 'depreciation'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Обесценивание денег (покупательная способность)
            </span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="calculationType"
              value="appreciation"
              checked={formData.calculationType === 'appreciation'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Рост стоимости с учетом инфляции
            </span>
          </label>
        </div>
      </div>

      {/* Начальный год */}
      <div>
        <label htmlFor="startYear" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-red-500" />
          Начальный год
        </label>
        <input
          type="number"
          id="startYear"
          name="startYear"
          value={formData.startYear}
          min={minYear}
          max={currentYear}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.startYear ? 'input-error' : ''}`}
          placeholder="Введите год"
        />
        {errors.startYear && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.startYear}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Доступны данные с {minYear} года
        </p>
      </div>

      {/* Конечный год */}
      <div>
        <label htmlFor="endYear" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-red-500" />
          Конечный год
        </label>
        <input
          type="number"
          id="endYear"
          name="endYear"
          value={formData.endYear}
          min={minYear}
          max={currentYear + 30}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.endYear ? 'input-error' : ''}`}
          placeholder="Введите год"
        />
        {errors.endYear && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.endYear}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Доступен прогноз до {currentYear + 5} года
        </p>
      </div>

      {/* Свои ставка инфляции */}
      <div className="col-span-full">
        <label className="inline-flex items-center mb-2">
          <input
            type="checkbox"
            name="showCustomRate"
            checked={formData.showCustomRate}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Использовать свою ставку инфляции
          </span>
        </label>

        {formData.showCustomRate && (
          <div className="flex">
            <div className="w-full">
              <div className="flex items-center">
                <input
                  type="text"
                  id="customInflationRate"
                  name="customInflationRate"
                  value={formData.customInflationRate}
                  onChange={handleInputChange}
                  className={`form-input w-full ${errors.customInflationRate ? 'input-error' : ''}`}
                  placeholder="Введите ставку инфляции"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">%</span>
              </div>
              {errors.customInflationRate && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.customInflationRate}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Будет применяться постоянная годовая ставка инфляции
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Использовать среднюю инфляцию */}
      <div className="col-span-full">
        <label className="inline-flex items-center mb-2">
          <input
            type="checkbox"
            name="useAverageInflation"
            checked={formData.useAverageInflation}
            onChange={handleInputChange}
            className="form-checkbox"
            disabled={formData.showCustomRate}
          />
          <span className={`ml-2 text-sm text-gray-700 dark:text-gray-300 ${formData.showCustomRate ? 'opacity-50' : ''}`}>
            Использовать среднюю инфляцию за период
          </span>
        </label>

        {formData.useAverageInflation && !formData.showCustomRate && (
          <div className="flex">
            <div className="w-full">
              <div className="flex items-center">
                <input
                  type="number"
                  id="averageYears"
                  name="averageYears"
                  value={formData.averageYears}
                  onChange={handleInputChange}
                  min="1"
                  max="50"
                  className={`form-input w-full ${errors.averageYears ? 'input-error' : ''}`}
                  placeholder="Количество лет"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">лет</span>
              </div>
              {errors.averageYears && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.averageYears}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Будет рассчитана средняя инфляция за указанное количество лет
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Категория товаров/услуг */}
      <div className="col-span-full">
        <label htmlFor="itemCategory" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Категория товаров/услуг
        </label>
        <select
          id="itemCategory"
          name="itemCategory"
          value={formData.itemCategory}
          onChange={handleInputChange}
          className="form-select w-full"
        >
          <option value="general">Общая инфляция</option>
          <option value="food">Продукты питания</option>
          <option value="housing">Жилье и коммунальные услуги</option>
          <option value="transport">Транспорт</option>
          <option value="medical">Медицинские услуги</option>
          <option value="education">Образование</option>
          <option value="clothing">Одежда и обувь</option>
          <option value="entertainment">Развлечения</option>
          <option value="electronics">Электроника</option>
          <option value="furniture">Мебель и бытовая техника</option>
        </select>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Для разных категорий товаров инфляция может отличаться от общей
        </p>
      </div>
    </div>
  );
};

export default BasicParametersSection;